import { Color, Spacing } from '@persona-id/design-tokens';
import { Duration } from 'luxon';
import { useMemo, useState, VFC } from 'react';
import styled from 'styled-components';

import HandlebarFormControl from 'dashboard/components/HandlebarFormControl';
import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import {
  filterUsableObjects,
  generateJsonSchemaConfig,
  UsableObjectConfig,
} from 'dashboard/lib/workflows';
import { Organization } from 'dashboard/models/organization';
import { Trigger } from 'dashboard/models/trigger';
import { WorkflowError, WorkflowFieldObjectKey } from 'dashboard/models/workflow';
import { WorkflowActionSmsInquiryLinkV1 } from 'dashboard/models/workflow-version';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import {
  DAYS_HOURS_MINUTES_HUMAN_FORMAT,
  HOURS_MINUTES_HUMAN_FORMAT,
  WEEKS_DAYS_HOURS_HUMAN_FORMAT,
} from 'lib/constants/luxonFormats';
import { JsonSchemaTypes } from 'lib/json-schema';

const DEFAULT_EXPIRES_IN_MINUTES = 60;

const Row = styled.div`
  display: flex;
  align-items: center;
`;

const PreviewContainer = styled.pre`
  border-color: ${({ theme }) => theme.color.border.input};
  border-width: 1px;
  border-style: solid;
  border-radius: ${({ theme }) => theme.element.borderRadius};
  padding: ${Spacing[3]} ${Spacing[4]};
  white-space: pre-wrap;
  background-color: ${Color.CoreSlate50};
`;

interface ParamsSmsInquiryLinkV1Props extends ParamsBaseProps {
  editable: boolean;
  errors: WorkflowError[];
  organization: Organization;
  params: WorkflowActionSmsInquiryLinkV1['params'];
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  updateActionParams: (newParams: WorkflowActionSmsInquiryLinkV1['params']) => void;
}

const ParamsSmsInquiryLinkV1: VFC<ParamsSmsInquiryLinkV1Props> = ({
  step,
  editable,
  errors,
  organization,
  params,
  trigger,
  usableObjects,
  updateActionParams,
  updateStep,
}) => {
  const [expiresInMinutes, setExpiresInMinutes] = useState(
    params['expires-in-minutes'] == null
      ? DEFAULT_EXPIRES_IN_MINUTES
      : params['expires-in-minutes'],
  );

  const { jsonSchema, tokensToDisplayValue } = useMemo(() => {
    return generateJsonSchemaConfig({
      usableObjects,
    });
  }, [usableObjects]);
  const inquiryOptions = useMemo(() => {
    return filterUsableObjects({
      usableObjects,
      options: {
        permittedObjects: [WorkflowFieldObjectKey.Inquiry],
        excludeNonPersonaObjects: true,
      },
    });
  }, [usableObjects]);
  const updateSmsInquiryLinkParams = (
    newParams: Partial<WorkflowActionSmsInquiryLinkV1['params']>,
  ) => {
    updateActionParams({
      ...params,
      ...newParams,
    });
  };

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionSmsInquiryLinkV1['params']>(errors);

  const durationLength =
    expiresInMinutes >= 10080
      ? Duration.fromObject({ minutes: expiresInMinutes }).toFormat(WEEKS_DAYS_HOURS_HUMAN_FORMAT)
      : expiresInMinutes >= 1440
        ? Duration.fromObject({ minutes: expiresInMinutes }).toFormat(
            DAYS_HOURS_MINUTES_HUMAN_FORMAT,
          )
        : Duration.fromObject({ minutes: expiresInMinutes }).toFormat(HOURS_MINUTES_HUMAN_FORMAT);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel labelText='Phone number' htmlFor='params-sms-inquiry-link__phone-number-input' />
      <HandlebarFormControl
        size='md'
        type='input'
        value={params['phone-number']}
        updateValue={(value: string) => {
          updateSmsInquiryLinkParams({ 'phone-number': value });
        }}
        editable={editable}
        allowedTypes={[JsonSchemaTypes.String]}
        jsonSchema={jsonSchema}
        tokensToDisplayValue={tokensToDisplayValue}
        id='params-sms-inquiry-link__phone-number-input'
        hasError={hasErrorFor('phone-number')}
      />
      {renderErrorMessageFor('phone-number')}
      <Spacer height={2} />
      <ParamLabel labelText='Inquiry' htmlFor='params-sms-inquiry-link__inquiry-input' />
      <ReactSelect
        value={params['inquiry']}
        options={inquiryOptions}
        onChange={(val: string) => updateSmsInquiryLinkParams({ inquiry: val })}
        disabled={!editable}
        id='params-sms-inquiry-link__inquiry'
        hasError={hasErrorFor('inquiry')}
      />
      {renderErrorMessageFor('inquiry')}
      <Spacer height={2} />
      <Row>
        <ParamLabel
          id='params-sms-inquiry-link__message--label'
          htmlFor='params-sms-inquiry-link__message'
          labelText='Message'
        />
        <HelpIcon>The inquiry link will be appended to the message below.</HelpIcon>
      </Row>
      <PreviewContainer
        id='params-sms-inquiry-link__message'
        data-test='params-sms-inquiry-link__message'
        aria-labelledby='params-sms-inquiry-link__message--label'
      >
        {/* Ensure this message is consistent with sms_inquiry_link_v1.rb on the BE */}
        Click the following link and complete the identity verification process for{' '}
        {organization.attributes.name} within{' '}
        {durationLength.replace(' 0 minutes', '').replace(' 0 hours', '').replace(' 0 days', '')}.
      </PreviewContainer>
      <Spacer height={2} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      >
        {' '}
        <Spacer height={2} />
        <ParamLabel
          labelText='Expires in minutes'
          htmlFor='params-sms-inquiry-link__expires-in-minutes-input'
        />
        <Input
          id='params-sms-inquiry-link__expires-in-minutes-input'
          type='number'
          min='1'
          value={String(expiresInMinutes || '')}
          onChange={(e) => {
            // Don't default to 0 when values are cleared as 0 is not a valid value
            const newValue = Number(e.target.value) > 0 ? Number(e.target.value) : null;
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            setExpiresInMinutes(newValue);
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            updateSmsInquiryLinkParams({ 'expires-in-minutes': newValue });
          }}
          disabled={!editable}
          placeholder={String(DEFAULT_EXPIRES_IN_MINUTES)}
          hasError={hasErrorFor('expires-in-minutes')}
        />
        {renderErrorMessageFor('expires-in-minutes')}
        <Spacer height={2} />
      </StepAdvancedConfigurationWithErrorHandlingPanel>
    </>
  );
};

export default ParamsSmsInquiryLinkV1;
